import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { compose } from "redux";
import { getInventory, deleteAsset, getAssetStats, addDependency, deleteDependency, getAssetStatsComp, getInventoryComp } from "../../store/actions/assetActions";
import { Pencil, Trash2, RotateCcw } from "lucide-react";
import { AlertCircle } from "lucide-react";

const DependencyMap = ({
  className = "",
  heightClass = "h-[calc(100vh-200px)]",
  onCanvasClick,
  assets,
  assets_data,
  getInventoryFN,
  getInventoryCompFN,
  deleteDependencyFN,
  addDependencyFN,
  getAssetStatsFN,
  getAssetStatsCompFN,

  department_id,
  compliance_id,
  isComp
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const panLayerRef = useRef(null);
  const scaledLayerRef = useRef(null);

  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);



  const [isLinking, setIsLinking] = useState(false);
  const linkFromIdRef = useRef(null);               // source node id
  const linkStartRef = useRef({ x: 0, y: 0 });      // world point
  const [linkPreview, setLinkPreview] = useState(null); // { sx, sy, ex, ey } in WORLD

  // panning refs
  const isPanningRef = useRef(false);
  const panStartRef = useRef({ x: 0, y: 0 });
  const pointerStartRef = useRef({ x: 0, y: 0 });

  // dragging nodes
  const [nodePositions, setNodePositions] = useState({}); // { [id]: {x,y} } in WORLD coords
  const draggingNodeRef = useRef(null);

  // layout helpers
  const categoryRowLabels = useMemo(
    () => [
     
    ],
    [t]
  );

  const categoryRowIndex = (category) => {
    switch (String(category || "").toLowerCase()) {
      case "hardware":
        return 0;
      case "software":
        return 1;
      case "services":
        return 2;
      case "virtual_machine":
        return 3;
      default:
        return 4;
    }
  };

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  useEffect(() => {
    if (isComp) {
      getInventoryCompFN(compliance_id)
      getAssetStatsCompFN(compliance_id)
    } else {
      getInventoryFN(department_id);
      getAssetStatsFN(department_id);
    }
  }, [getInventoryCompFN, getAssetStatsCompFN, getInventoryFN, getAssetStatsFN, department_id, compliance_id]);


  const [editMode, setEditMode] = useState("none"); // "none" | "draw" | "delete"
  const [pendingFromId, setPendingFromId] = useState(null);

  const getNodePorts = (idToPos, id) => {
    const p = idToPos[id];
    if (!p) return null;

    return {
      right: { x: p.x + NODE_W + 6, y: p.y + NODE_H / 2 },
      left: { x: p.x - 6, y: p.y + NODE_H / 2 },
    };
  };

  const getPointerWorld = (e) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return { wx: 0, wy: 0 };

    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;

    // screen -> world
    const wx = (sx - pan.x) / zoom;
    const wy = (sy - pan.y) / zoom;

    return { wx, wy };
  };

  // ===== Canvas pointer handlers =====
  const onPointerDown = (e) => {
    if (e.button !== 0) return;

    // clicked a node? don't pan.
    if (e.target.closest?.("[data-bt-node='true']")) return;

    e.preventDefault(); // ✅ prevents native drag/select weirdness
    e.currentTarget.setPointerCapture(e.pointerId);

    isPanningRef.current = true;
    panStartRef.current = { ...pan };
    pointerStartRef.current = { x: e.clientX, y: e.clientY };
  };

  const onPointerMove = (e) => {

    if (isLinking) {
      const { wx, wy } = getPointerWorld(e);
      setLinkPreview((prev) =>
        prev ? { ...prev, ex: wx, ey: wy } : { sx: wx, sy: wy, ex: wx, ey: wy }
      );
      return; // do NOT pan while drawing
    }
    // 1) dragging a node?
    if (draggingNodeRef.current) {
      const { nodeId, startWorld, startPos } = draggingNodeRef.current;
      const { wx, wy } = getPointerWorld(e);

      const dx = wx - startWorld.x;
      const dy = wy - startWorld.y;

      setNodePositions((prev) => ({
        ...prev,
        [nodeId]: { x: startPos.x + dx, y: startPos.y + dy },
      }));
      return;
    }

    // 2) panning background?
    if (!isPanningRef.current) return;

    const dx = e.clientX - pointerStartRef.current.x;
    const dy = e.clientY - pointerStartRef.current.y;

    setPan({ x: panStartRef.current.x + dx, y: panStartRef.current.y + dy });
  };

  const onPointerUp = (e) => {

    if (isLinking) {
      setIsLinking(false);

      const fromId = linkFromIdRef.current;
      linkFromIdRef.current = null;

      setLinkPreview(null);

      // find target node under pointer
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const targetNodeEl = el?.closest?.("[data-bt-node='true']");
      const toId = targetNodeEl?.getAttribute?.("data-node-id");

      if (fromId && toId && String(fromId) !== String(toId)) {
        handleAddDependancy(fromId, toId);
        setEditMode("none"); // optional: auto-exit draw mode
      }

      // important: release capture
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch { }

      return;
    }

    draggingNodeRef.current = null;
    isPanningRef.current = false;

    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch { }
  };

  React.useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;

    const handleWheel = (e) => {
      // stop the page/parent scroll
      e.preventDefault();
      e.stopPropagation();

      const rect = el.getBoundingClientRect();
      const cursorX = e.clientX - rect.left;
      const cursorY = e.clientY - rect.top;

      const delta = -e.deltaY;
      const zoomFactor = delta > 0 ? 1.06 : 1 / 1.06;

      setZoom((prevZoom) => {
        const nextZoom = clamp(prevZoom * zoomFactor, 0.25, 2.5);

        // keep world point under cursor stable
        setPan((prevPan) => {
          const worldX = (cursorX - prevPan.x) / prevZoom;
          const worldY = (cursorY - prevPan.y) / prevZoom;

          return {
            x: cursorX - worldX * nextZoom,
            y: cursorY - worldY * nextZoom,
          };
        });

        return nextZoom;
      });
    };

    el.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      el.removeEventListener("wheel", handleWheel);
    };
  }, [canvasRef, clamp]);


  // ===== Node drag start =====
  const onNodePointerDown = (e, nodeId, fallbackPos) => {
    e.stopPropagation();

    // 🎯 DRAW MODE: drag to connect
    if (editMode === "draw") {
      e.preventDefault();
      e.stopPropagation();

      // start linking
      setIsLinking(true);
      linkFromIdRef.current = nodeId;

      // capture pointer so we keep getting move/up even if leaving node
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch { }

      // set initial preview start/end
      // start at source node right port (we'll compute from idToPos during render, so start with pointer)
      const { wx, wy } = getPointerWorld(e);
      linkStartRef.current = { x: wx, y: wy };
      setLinkPreview({ sx: wx, sy: wy, ex: wx, ey: wy });

      return;
    }

    // 🚫 DELETE MODE: do nothing on node
    if (editMode === "delete") {
      return;
    }

    // 🧭 NORMAL MODE: drag node
    e.preventDefault();

    const { wx, wy } = getPointerWorld(e);
    const cur = nodePositions[nodeId] ?? fallbackPos;

    draggingNodeRef.current = {
      nodeId,
      startWorld: { x: wx, y: wy },
      startPos: cur,
    };
  };






  const onCanvasClickLocal = (e) => {
    // ✅ if you clicked a node, DO NOT cancel draw selection
    if (e.target.closest?.("[data-bt-node='true']")) {
      return;
    }

    // ✅ only empty-space click cancels the pending selection
    if (editMode === "draw") setPendingFromId(null);

    onCanvasClick?.(e);
  };

  const onResetLayout = () => {
    setNodePositions({});
    setPan({ x: 0, y: 0 });
    setZoom(1);
    setPendingFromId(null);
  };
  // ===== initial layout (category rows + x spacing) =====
  const xCountByCategory = {}; // reset every render (important)


  const NODE_W = 210;
  const NODE_H = 92; // tweak if your card is taller/shorter

  const normalizeDepId = (d) => {
    if (d == null) return null;
    if (typeof d === "string" || typeof d === "number") return d;
    return d.id ?? d.assetId ?? d.dependency_id ?? d.dependencyId ?? null;
  };

  const getNodeTopLeft = (a) => {
    const id = a.id;
    const cat = a.category || "unknown";

    const idx = xCountByCategory[cat] ?? 0;
    xCountByCategory[cat] = idx + 1;

    const row = categoryRowIndex(cat);

    const startX = 220;
    const colGap = 240;
    const rowGap = 150;
    const topBase = 30;

    const basePos = {
      x: startX + idx * colGap,
      y: topBase + row * rowGap,
    };

    const saved = nodePositions[id];
    return {
      pos: { x: saved?.x ?? basePos.x, y: saved?.y ?? basePos.y },
      basePos,
    };
  };

  const handleAddDependancy = async (to, from) => {
    ////console.log("Adding dep ", to, from)
    await addDependencyFN(to, from)
    if (isComp) {
      getInventoryCompFN(compliance_id)
      getAssetStatsCompFN(compliance_id)
    } else {
      getInventoryFN(department_id);
      getAssetStatsFN(department_id);
    }
  }


  const handleDeleteDependancy = async (to, from) => {
    ////console.log("Deleting dep ", to, from)
    await deleteDependencyFN(to, from)
    if (isComp) {
      getInventoryCompFN(compliance_id)
      getAssetStatsCompFN(compliance_id)
    } else {
      getInventoryFN(department_id);
      getAssetStatsFN(department_id);
    }
  }

  return (
    <div className={`relative flex w-full h-full ${className}`}>

      <div
        className="absolute top-3 left-1/2 -translate-x-1/2 z-[100]
             flex items-center gap-1.5
             bg-white/95 backdrop-blur
             border border-gray-200 rounded-xl
             shadow-sm px-2 py-1"
      >
        {/* Draw */}
        <button
          onClick={() => {
            setEditMode((m) => (m === "draw" ? "none" : "draw"));
            setPendingFromId(null);
          }}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition
      ${editMode === "draw"
              ? "bg-blue-600 text-white shadow-sm"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
        >
          <Pencil className="w-3.5 h-3.5" />
          {t("assetManagement.dependencyMapPage.canvas.draw")}
        </button>

        {/* Delete */}
        <button
          onClick={() => {
            setEditMode((m) => (m === "delete" ? "none" : "delete"));
            setPendingFromId(null);
          }}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition
      ${editMode === "delete"
              ? "bg-red-600 text-white shadow-sm"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
          {t("assetManagement.dependencyMapPage.canvas.delete")}
        </button>

        {/* Divider */}
        <div className="w-px h-5 bg-gray-300 mx-1" />

        {/* Reset */}
        <button
          onClick={onResetLayout}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg
               bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          {t("assetManagement.dependencyMapPage.canvas.reset")}
        </button>
      </div>


      {/* ===== MAP CANVAS ===== */}
      <div
        ref={canvasRef}
        draggable={false}
        onDragStart={(e) => e.preventDefault()}
        onClick={onCanvasClickLocal}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        className={`flex-grow ${heightClass} border border-gray-300 rounded-lg relative overflow-hidden
                  cursor-grab active:cursor-grabbing`}
        style={{ touchAction: "none", userSelect: "none" }}
      >
        {/* Fixed category labels */}
        <div className="absolute top-6 left-4 z-50 pointer-events-none flex flex-col gap-[122px]">
          {categoryRowLabels.map((label) => (
            <div key={label} className="text-xs font-semibold text-gray-500 tracking-wide select-none">
              {label}
            </div>
          ))}
        </div>

        {/* dotted grid */}
        <div
          className="absolute inset-0 z-0 pointer-events-none"
          style={{
            backgroundColor: "#ffffff",
            backgroundImage: "radial-gradient(#e5e7eb 1px, transparent 1px)",
            backgroundSize: "18px 18px",
            backgroundPosition: "0 0",
            backgroundAttachment: "local",
          }}
        />

        {/* Pan layer */}
        <div
          ref={panLayerRef}
          className="absolute inset-0 z-5 will-change-transform"
          style={{ transform: `translate(${pan.x}px, ${pan.y}px)`, transition: "none" }}
        >
          {/* Scaled layer */}
          <div
            ref={scaledLayerRef}
            className="absolute inset-0 z-10 origin-top-left"
            style={{ transform: `scale(${zoom})`, transition: "none" }}
          >
            {/* content surface */}
            <div className="relative w-full h-full">
              {(() => {
                const idToPos = {};
                const idToBase = {};

                const laneCounts = {};

                assets?.forEach((a) => {
                  const { pos, basePos } = getNodeTopLeft(a);

                  const rawType = String(
                    a.asset_type ??
                    a.type ??
                    a.category ??
                    a.AssetType ??
                    a.Type ??
                    "other"
                  )
                    .trim()
                    .toLowerCase()
                    .replace(/\s+/g, "");

                  let laneKey = "other";

                  if (rawType === "software") laneKey = "software";
                  else if (rawType === "hardware") laneKey = "hardware";
                  else if (rawType === "virtualmachine") laneKey = "virtualmachine";

                  const laneIndex = laneCounts[laneKey] ?? 0;
                  const xOffset = laneIndex * 220; // tweak if needed

                  const hasSavedPos = !!nodePositions[a.id];

                  idToPos[a.id] = {
                    ...pos,
                    // only apply xOffset when there is no saved position; a saved
                    // position was already stored with xOffset included, so adding
                    // it again causes the card to jerk on the first drag move
                    x: hasSavedPos ? pos.x : pos.x + xOffset,
                  };

                  idToBase[a.id] = {
                    ...basePos,
                    x: basePos.x + xOffset,
                  };

                  laneCounts[laneKey] = laneIndex + 1;
                });

                const clickable = editMode === "delete"; // ✅ arrow click deletes

                return (
                  <>
                    {/* Arrows layer (world space) */}
                    <svg
                      className="absolute inset-0 z-[1] overflow-visible"
                      style={{ pointerEvents: clickable ? "auto" : "none" }}
                    >
                      <defs>
                        <marker
                          id="arrowHead"
                          markerWidth="10"
                          markerHeight="10"
                          refX="9"
                          refY="5"
                          orient="auto"
                          markerUnits="strokeWidth"
                        >
                          <path d="M 0 0 L 10 5 L 0 10 z" fill="#9ca3af" />
                        </marker>
                      </defs>

                      {assets?.flatMap((a) => {
                        const from = idToPos[a.id];
                        if (!from) return [];

                        const depIds = (Array.isArray(a.dependencies) ? a.dependencies : [])
                          .map(normalizeDepId)
                          .filter(Boolean);

                        return depIds.map((toId) => {
                          const to = idToPos[toId];
                          if (!to) return null;

                          const sx = from.x + NODE_W + 6;
                          const sy = from.y + NODE_H / 2;
                          const ex = to.x - 6;
                          const ey = to.y + NODE_H / 2;

                          const dx = Math.max(80, Math.min(220, (ex - sx) / 2));
                          const d = `M ${sx} ${sy} C ${sx + dx} ${sy}, ${ex - dx} ${ey}, ${ex} ${ey}`;

                          return (
                            <g key={`${a.id}->${toId}`}>
                              {/* fat invisible hit area for delete mode */}
                              <path
                                data-bt-edge="true"
                                d={d}
                                fill="none"
                                stroke="transparent"
                                strokeWidth="14"
                                style={{
                                  pointerEvents: clickable ? "stroke" : "none",
                                  cursor: clickable ? "pointer" : "default",
                                }}
                                onPointerDown={(e) => {
                                  if (!clickable) return;
                                  e.preventDefault();
                                  e.stopPropagation();   // ✅ stops canvas from capturing pointer
                                }}
                                onClick={(e) => {
                                  if (!clickable) return;
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleDeleteDependancy(a.id, toId);
                                  setEditMode("none");
                                }}
                              />


                              {/* visible arrow */}
                              <path
                                d={d}
                                fill="none"
                                stroke="#9ca3af"
                                strokeWidth="2"
                                markerEnd="url(#arrowHead)"
                                opacity="0.9"
                                style={{ pointerEvents: "none" }}
                              />
                            </g>
                          );
                        });
                      })}

                      {/* Live preview arrow while drawing */}
                      {editMode === "draw" && isLinking && linkPreview && (() => {
                        const fromId = linkFromIdRef.current;
                        if (!fromId) return null;

                        const ports = getNodePorts(idToPos, fromId);
                        if (!ports) return null;

                        // snap start to RIGHT port
                        const sx = ports.right.x;
                        const sy = ports.right.y;

                        // end is current pointer world
                        const ex = linkPreview.ex;
                        const ey = linkPreview.ey;

                        const dx = Math.max(80, Math.min(220, (ex - sx) / 2));
                        const d = `M ${sx} ${sy} C ${sx + dx} ${sy}, ${ex - dx} ${ey}, ${ex} ${ey}`;

                        return (
                          <path
                            d={d}
                            fill="none"
                            stroke="#3b82f6"
                            strokeWidth="2.5"
                            markerEnd="url(#arrowHead)"
                            opacity="0.95"
                            strokeDasharray="6 4"
                            style={{ pointerEvents: "none" }}
                          />
                        );
                      })()}

                    </svg>



                    {/* Nodes layer */}
                    <div className="relative z-[2]">
                      {assets?.map((a) => {
                        const pos = idToPos[a.id];
                        const basePos = idToBase[a.id];

                        return (
                          <AssetNode
                            key={a.id}
                            asset={a}
                            allAssets={assets}
                            style={{ left: pos.x, top: pos.y }}
                            onPointerDown={(e) => onNodePointerDown(e, a.id, basePos)}
                            isSelected={editMode === "draw" && pendingFromId === a.id}
                          />
                        );
                      })}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {/* HUD */}
        <div className="absolute bottom-3 right-3 z-50 px-2 py-1 rounded-md bg-white/90 border text-xs text-gray-700 pointer-events-none">
          x: {Math.round(pan.x)} y: {Math.round(pan.y)} · zoom: {zoom.toFixed(2)}
        </div>
      </div>
    </div>
  );

};
const countIncomingFromAssets = (assetId, allAssets) => {
  if (!Array.isArray(allAssets)) return 0;

  return allAssets.reduce((acc, a) => {
    if (!Array.isArray(a?.dependencies)) return acc;
    return acc + (a.dependencies.includes(assetId) ? 1 : 0);
  }, 0);
};

const AssetNode = ({ asset, allAssets, style, onPointerDown, isSelected }) => {
  const { t } = useTranslation();
  const categoryLabelMap = useMemo(
    () => ({
      hardware: t("assetManagement.category.hardware"),
      software: t("assetManagement.category.software"),
      services: t("assetManagement.dependencyMapPage.rowLabels.services"),
      virtual_machine: t("assetManagement.dependencyMapPage.rowLabels.virtualMachine"),
    }),
    [t]
  );

  const getCiaScoreColor = (score) => {
    if (!score) return "bg-gray-400";
    if (score >= 75) return "bg-red-600";
    if (score >= 40) return "bg-amber-600";
    return "bg-emerald-600";
  };

  const ciaDotColor = getCiaScoreColor(asset?.ciaScore);

  // 🟢 outgoing
  const outgoingCount = Array.isArray(asset?.dependencies)
    ? asset.dependencies.length
    : 0;

  // 🔵 incoming (computed)
  const incomingCount = countIncomingFromAssets(asset?.id, allAssets);

  return (
    <div
      data-bt-node="true"
      data-node-id={asset?.id}
      onPointerDown={onPointerDown}
      className="absolute select-none"
      style={style}
    >
      <div
        className={`relative w-[210px] bg-white border border-gray-200 rounded-xl shadow-sm px-3 py-2
        ${isSelected ? "ring-2 ring-blue-600" : ""}`}
      >
        {/* 🔵 Incoming (middle LEFT, smaller) */}
        <div
          className="
            absolute left-[-10px] top-1/2 -translate-y-1/2
            w-5 h-5 rounded-full bg-blue-500 text-white
            text-[10px] font-semibold flex items-center justify-center
          "
          title="Incoming dependencies"
        >
          {incomingCount}
        </div>

        {/* 🟢 Outgoing (middle RIGHT, smaller) */}
        <div
          className="
            absolute right-[-10px] top-1/2 -translate-y-1/2
            w-5 h-5 rounded-full bg-green-500 text-white
            text-[10px] font-semibold flex items-center justify-center
          "
          title="Outgoing dependencies"
        >
          {outgoingCount}
        </div>

        {/* top row */}
        <div className="flex items-center gap-2">
          <span
            className={`w-2.5 h-2.5 rounded-full ${ciaDotColor}`}
            title={`CIA Score: ${asset?.ciaScore ?? "N/A"}`}
          />
          <span className="text-[11px] font-semibold text-gray-700">
            {asset?.typeId}
          </span>
        </div>

        {/* title */}
        <div className="mt-2">
          <div className="text-[13px] font-semibold text-gray-900 leading-snug">
            {asset?.name}
          </div>
          <span className="text-[11px] text-gray-500">
            {categoryLabelMap[asset?.category] ?? asset?.category ?? "-"}
          </span>
        </div>

        {/* bottom row */}
        <div className="mt-2 flex items-center justify-between">
          <div className="flex items-center gap-1 text-[11px] text-gray-500">
            <span className="text-gray-400">🔗</span>
            <span>{outgoingCount}</span>
          </div>

          <div className="flex items-center gap-1 text-[11px] text-gray-600">
            <span className="w-2 h-2 rounded-full bg-purple-500" />
            <span className="font-medium">{asset?.ciaScore ?? 0}</span>
          </div>
        </div>
      </div>
    </div>
  );
};



const countIncoming = (nodeId, edges) =>
  edges.reduce(
    (acc, e) => acc + (String(e.target) === String(nodeId) ? 1 : 0),
    0
  );

const countOutgoing = (nodeId, edges) =>
  edges.reduce(
    (acc, e) => acc + (String(e.source) === String(nodeId) ? 1 : 0),
    0
  );


const levelToScore = (value) => {
  if (!value) return 0;
  const v = String(value).toLowerCase();
  switch (v) {
    case "insignificant": return 1;
    case "minor": return 2;
    case "moderate": return 3;
    case "major": return 4;
    case "catastrophe": return 5;
    case "unassessed": return 0;
    default: return 0;
  }
};

const computeCiaScore = (a) => {
  const c = levelToScore(a?.confidentiality);
  const i = levelToScore(a?.integrity);
  const av = levelToScore(a?.availability);
  return c > 0 && i > 0 && av > 0 ? c * i * av : 0;
};

const normalizeDepId = (d) => {
  if (d == null) return null;
  if (typeof d === "string" || typeof d === "number") return d;
  return d.id ?? d.assetId ?? d.dependency_id ?? d.dependencyId ?? null;
};

const getImpactMeta = (n, t) => {
  if (n >= 6) return { label: t("assetManagement.spof.impactLevel.critical", { count: n }), pill: "bg-red-50 text-red-700 border-red-200" };
  if (n >= 3) return { label: t("assetManagement.spof.impactLevel.high", { count: n }), pill: "bg-orange-50 text-orange-700 border-orange-200" };
  if (n >= 2) return { label: t("assetManagement.spof.impactLevel.medium", { count: n }), pill: "bg-amber-50 text-amber-700 border-amber-200" };
  return { label: t("assetManagement.spof.impactLevel.low", { count: n }), pill: "bg-gray-50 text-gray-700 border-gray-200" };
};

const getCiaMeta = (cia, t) => {
  if (cia >= 100) return { pill: "bg-red-600 text-white", label: t("assetManagement.spof.ciaLevel.critical") };
  if (cia >= 60) return { pill: "bg-orange-500 text-white", label: t("assetManagement.spof.ciaLevel.high") };
  if (cia >= 30) return { pill: "bg-amber-500 text-white", label: t("assetManagement.spof.ciaLevel.medium") };
  return { pill: "bg-gray-400 text-white", label: t("assetManagement.spof.ciaLevel.low") };
};

export function SpofPanel({ assets = [], onViewMap, isComp }) {
  const { t } = useTranslation();
  const {
    spofs,
    totalSpofs,
    highestImpact,
    avgRiskScore,
  } = React.useMemo(() => {
    const idToAsset = new Map();
    (assets || []).forEach((a) => idToAsset.set(a.id, a));

    // Build incoming edges (dependents) map: targetId -> [{id,name}]
    const incoming = new Map();
    (assets || []).forEach((src) => {
      const deps = Array.isArray(src?.dependencies) ? src.dependencies : [];
      deps.map(normalizeDepId).filter(Boolean).forEach((targetId) => {
        const arr = incoming.get(targetId) ?? [];
        arr.push({ id: src.id, name: src.name, typeId: src.typeId });
        incoming.set(targetId, arr);
      });
    });
    ////console.log("Assets ", assets)
    const rows = (assets || [])
      .map((a) => {
        const dependents = incoming.get(a.id) ?? [];
        const depCount = dependents.length;
        ////console.log("depCount ", depCount)
        const cia = computeCiaScore(a);
        const combinedRisk = cia * depCount;

        return {
          id: a.id,
          typeId: a.typeId,
          name: a.name,
          category: a.category,
          department: a.department,
          location: a.location,
          dependents,
          depCount,
          cia,
          combinedRisk,
        };
      })
      .filter((r) => r.depCount >= 2)
      .sort((a, b) => b.combinedRisk - a.combinedRisk);

    const total = rows.length;
    const highest = rows.reduce((m, r) => Math.max(m, r.depCount), 0);
    const avg = total > 0
      ? Math.round(rows.reduce((s, r) => s + r.combinedRisk, 0) / total)
      : 0;

    return {
      spofs: rows,
      totalSpofs: total,
      highestImpact: highest,
      avgRiskScore: avg,
    };
  }, [assets]);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mb-4">
      {/* Header */}
      <div className="px-6 py-5 border-b border-gray-200 flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
          <AlertCircle className="w-5 h-5 text-red-600" />
        </div>
        <div>
          <div className="text-[15px] font-semibold text-gray-900">
            {t("assetManagement.spof.title")}
          </div>
          <div className="text-sm text-gray-500">
            {t("assetManagement.spof.description")}
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="px-6 py-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-2xl border border-gray-200 px-5 py-4 flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-500">{t("assetManagement.spof.totalSpofs")}</div>
              <div className="text-2xl font-semibold text-red-600 mt-1">{totalSpofs}</div>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 px-5 py-4 flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-500">{t("assetManagement.spof.highestImpact")}</div>
              <div className="text-2xl font-semibold text-orange-600 mt-1">
                {t("assetManagement.spof.assetsCount", { count: highestImpact })}
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 px-5 py-4 flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-500">{t("assetManagement.spof.avgRiskScore")}</div>
              <div className="text-2xl font-semibold text-purple-600 mt-1">{avgRiskScore}</div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="mt-4 overflow-x-auto rounded-2xl border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr className="border-b border-gray-200">
                <th className="px-4 py-3 text-left w-12">#</th>
                <th className="px-4 py-3 text-left">{t("assetManagement.spof.table.asset")}</th>
                <th className="px-4 py-3 text-left">{t("assetManagement.spof.table.categoryDept")}</th>
                <th className="px-4 py-3 text-left">{t("assetManagement.spof.table.impact")}</th>
                <th className="px-4 py-3 text-left">{t("assetManagement.spof.table.dependentAssets")}</th>
                <th className="px-4 py-3 text-right">{t("assetManagement.spof.table.ciaScore")}</th>
                <th className="px-4 py-3 text-right">{t("assetManagement.spof.table.combinedRisk")}</th>
                <th className="px-4 py-3 text-right">{t("assetManagement.spof.table.action")}</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {spofs.map((r, idx) => {
                const impact = getImpactMeta(r.depCount, t);
                const cia = getCiaMeta(r.cia, t);

                return (
                  <tr key={r.id} className="bg-white">
                    <td className="px-4 py-4">
                      <div className="w-7 h-7 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center text-xs font-semibold">
                        {idx + 1}
                      </div>
                    </td>

                    <td className="px-4 py-4">
                      <div className="text-xs text-gray-500">{r.typeId ?? `AST-${r.id}`}</div>
                      <div className="font-semibold text-gray-900">{r.name}</div>
                    </td>

                    <td className="px-4 py-4">
                      <div className="text-xs text-gray-500 capitalize">{r.category || "-"}</div>
                      <div className="text-xs text-gray-500">{r.department || r.location || "-"}</div>
                    </td>

                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${impact.pill}`}>
                        {impact.label}
                      </span>
                    </td>

                    <td className="px-4 py-4">
                      <div className="flex flex-wrap gap-2">
                        {r.dependents.slice(0, 6).map((d) => (
                          <span
                            key={d.id}
                            className="px-2 py-1 rounded-full border border-gray-200 bg-white text-xs text-gray-700"
                            title={d.typeId || ""}
                          >
                            {d.name}
                          </span>
                        ))}
                        {r.dependents.length > 6 && (
                          <span className="px-2 py-1 rounded-full border border-gray-200 bg-gray-50 text-xs text-gray-600">
                            {t("assetManagement.spof.moreDependents", { count: r.dependents.length - 6 })}
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="px-4 py-4 text-right">
                      <div className="font-semibold text-purple-700">{r.cia}</div>
                      <span className={`inline-flex mt-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${cia.pill}`}>
                        {cia.label}
                      </span>
                    </td>

                    <td className="px-4 py-4 text-right">
                      <div className="font-semibold text-orange-700">{r.combinedRisk}</div>
                      <div className="text-[11px] text-gray-500">{t("assetManagement.spof.combinedRiskHint")}</div>
                    </td>

                    <td className="px-4 py-4 text-right">
                      <button
                        onClick={() => onViewMap?.(r.id)}
                        className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 bg-white hover:bg-gray-50"
                      >
                        {t("assetManagement.spof.viewMap")}
                      </button>
                    </td>
                  </tr>
                );
              })}

              {spofs.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-gray-500">
                    {t("assetManagement.spof.emptyState")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    assets: state.asset.assets,
    error: state.asset.error,
    sidebar_collapsed: state.auth.sidebar_collapsed,
    assets_data: state.asset.assets_stat_data,
    department_id: state.department.activeDepartmentId,
    compliance_id: state.compliance.activeComplianceId
  };
};

const mapDispatchToProps = (dispatch) => ({
  getInventoryFN: (dep) => dispatch(getInventory(dep)),
  deleteAssetFN: (id) => dispatch(deleteAsset(id)),
  getAssetStatsFN: (dep) => dispatch(getAssetStats(dep)),
  addDependencyFN: (to, from) => dispatch(addDependency(to, from)),
  deleteDependencyFN: (to, from) => dispatch(deleteDependency(to, from)),
  getInventoryCompFN: (comp) => dispatch(getInventoryComp(comp)),
  getAssetStatsCompFN: (comp) => dispatch(getAssetStatsComp(comp)),
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(DependencyMap);
